// src/contexts/NotificationsContext.js
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import {
  subscribeToNotifications,
  subscribeToUnreadMessages,
  markNotificationRead,
  markAllNotificationsRead,
  fetchUserProfile
} from '../features/collaboration/services/collaborationService';

const NotificationsContext = createContext();

export const useNotifications = () => useContext(NotificationsContext);

const buildCounts = (notifications) => {
  const counts = {
    connection: 0,
    group: 0,
    event: 0,
    activity: 0
  };
  notifications.forEach((note) => {
    if (!note || note.read) return;
    if (counts[note.type] !== undefined) {
      counts[note.type] += 1;
    }
  });
  return counts;
};

export const NotificationsProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [actorProfiles, setActorProfiles] = useState({});

  useEffect(() => {
    if (!currentUser) {
      setNotifications([]);
      return undefined;
    }
    const unsubscribe = subscribeToNotifications(currentUser.uid, setNotifications);
    return () => unsubscribe && unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      setUnreadMessagesCount(0);
      return undefined;
    }
    const unsubscribe = subscribeToUnreadMessages(currentUser.uid, setUnreadMessagesCount);
    return () => unsubscribe && unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    if (!notifications.length) return;
    const missingIds = Array.from(
      new Set(
        notifications
          .map((note) => note.actorId)
          .filter((id) => id && !actorProfiles[id])
      )
    );
    if (!missingIds.length) return;

    let isActive = true;
    const loadProfiles = async () => {
      const updates = {};
      for (const id of missingIds) {
        try {
          const profile = await fetchUserProfile(id);
          if (profile) updates[id] = profile;
        } catch (error) {
          console.error('NotificationsContext: failed to fetch actor profile', error);
        }
      }
      if (isActive && Object.keys(updates).length) {
        setActorProfiles((prev) => ({ ...prev, ...updates }));
      }
    };
    loadProfiles();
    return () => {
      isActive = false;
    };
  }, [notifications, actorProfiles]);

  const unreadNotificationsCount = useMemo(
    () => notifications.filter((note) => note && !note.read).length,
    [notifications]
  );
  const notificationCounts = useMemo(
    () => buildCounts(notifications),
    [notifications]
  );
  const totalBadgeCount = unreadNotificationsCount + (unreadMessagesCount || 0);

  const handleMarkAllRead = async () => {
    if (!currentUser) return false;
    return markAllNotificationsRead(currentUser.uid);
  };

  const value = {
    notifications,
    actorProfiles,
    unreadMessagesCount,
    unreadNotificationsCount,
    totalBadgeCount,
    notificationCounts,
    markNotificationRead,
    markAllNotificationsRead: handleMarkAllRead
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};
